import React, { useCallback } from 'react';
import type { Call } from '@sc1/common/gql/types';
import {
  formatDate,
  useSend,
  useDecodeTx,
  isAddress,
  IChain,
  getStatus,
  getExplorerUrl,
  shortenAddress,
  ScionTimelock,
  useCall2,
} from '@sc1/common';
import { Box, Row, Table, Text, ExternalLink } from '../core';
import { ShortenedAddress } from '../ShortenedAddress';
import { ExternalLinkIcon } from '../icons';
import { StatusButton } from '../StatusButton';
import { MouseoverTooltipContent, TooltipContainer } from '../Tooltip';

interface ExecutableTxProps {
  call: Partial<Call>;
  chain: IChain;
  timelock: ScionTimelock | undefined;
  disabled: boolean;
}

export const ExecutableTx = ({
  call,
  chain,
  timelock,
  disabled,
}: ExecutableTxProps): JSX.Element | null => {
  const {
    id,
    target,
    value,
    data,
    predecessor,
    salt,
    createdAt,
    readyAt,
    executedAt,
    cancelledAt,
    txAddress,
    secondTxAddress,
  } = call;
  const { fn, args, targetName } = useDecodeTx(chain, call);
  const { sendTx, txStatus } = useSend();
  const { sendTx: sendTx1, txStatus: txStatus1 } = useSend();

  const cancelTx = useCallback(
    () => timelock && id != null && sendTx(timelock, 'cancel', [id]),
    [timelock, id, sendTx]
  );

  const executeTx = useCallback(
    () =>
      timelock &&
      target &&
      value &&
      data &&
      predecessor &&
      salt &&
      sendTx1(timelock, 'execute', [target, value, data, predecessor, salt]),
    [timelock, target, value, data, predecessor, salt, sendTx1]
  );
  const { res: isOperationPending } = useCall2(
    timelock?.isOperationPending,
    id!
  );
  const { res: isOperationDone } = useCall2(timelock?.isOperationDone, id!);
  const { res: isOperationReady } = useCall2(timelock?.isOperationReady, id!);

  const status = getStatus({
    isOperationDone,
    isOperationPending,
    isOperationReady,
    cancelledAt,
    readyAt,
    executedAt,
  });

  const wasCancelled = cancelledAt && cancelledAt !== '0';
  const wasExecuted = executedAt && executedAt !== '0';

  const explorer = getExplorerUrl(chain);
  const color = isOperationPending
    ? 'text'
    : isOperationDone
    ? 'border'
    : 'muted';

  const getTxInfo = (
    date: string | undefined,
    addr?: string | false | undefined
  ) => {
    const dateFmt = formatDate(Number(date) * 1000);
    if (!addr) return dateFmt;
    return (
      <ExternalLink sx={{ color: 'secondary' }} href={`${explorer}tx/${addr}`}>
        <Text mr={1}>{dateFmt}</Text>
        <ExternalLinkIcon />
      </ExternalLink>
    );
  };
  return (
    <tr
      sx={{
        td: {
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'border',
          p: '5px 10px 5px 0px',
          maxWidth: 200,
          wordWrap: 'break-word',
        },
        color,
      }}
    >
      <td>
        <ExternalLink href={`${explorer}address/${target}`} sx={{ color }}>
          <Text mr={1}>{targetName ?? (target && shortenAddress(target))}</Text>
          <ExternalLinkIcon />
        </ExternalLink>
      </td>
      <td>{fn?.name || 'Unknown Fn'}</td>
      <td>
        {args?.map((x, i) => (
          <Box key={x + '--' + i}>
            {fn?.functionFragment?.inputs?.[i]?.name}:{' '}
            {isAddress(x) ? (
              <ShortenedAddress ml={1} characters={2} address={x} />
            ) : (
              x
            )}
          </Box>
        )) || 'Unknown Variables'}
      </td>
      <td>
        <MouseoverTooltipContent
          placement="top"
          width="auto"
          content={
            <TooltipContainer>
              <Table
                columns={[2]}
                borderBottom={false}
                borderTop={false}
                index={false}
                data={[
                  ['Created', getTxInfo(createdAt, txAddress)],
                  ['Ready', getTxInfo(readyAt)],
                  [
                    'Cancelled',
                    getTxInfo(cancelledAt, wasCancelled && secondTxAddress),
                  ],
                  [
                    'Executed',
                    getTxInfo(executedAt, wasExecuted && secondTxAddress),
                  ],
                ]}
              />
            </TooltipContainer>
          }
        >
          <ExternalLink
            sx={{ color }}
            href={`${explorer}tx/${secondTxAddress ?? txAddress}`}
          >
            <Text mr={1}>{status}</Text>
            <ExternalLinkIcon />
          </ExternalLink>
        </MouseoverTooltipContent>
      </td>
      <td>
        <Row>
          <StatusButton
            variant="miniStatusButton"
            onClick={cancelTx}
            disabled={disabled || !isOperationPending}
            status={txStatus}
            mr={[1, 2]}
          >
            Cancel
          </StatusButton>
          <StatusButton
            disabled={disabled || !isOperationReady}
            variant="miniStatusButton"
            onClick={executeTx}
            status={txStatus1}
          >
            Execute
          </StatusButton>
        </Row>
      </td>
    </tr>
  );
};
